/**
 * Hand-authored Gen-3 flavor + species facts for v0.1 species. Powers the
 * dossier ABOUT tab's prose paragraphs (the flavor pull-quote + the
 * height / color / growth-rate sentence).
 *
 * Eng review EN-2 / EN-3 (2026-05-13): the design originally referenced
 * `Dex.species.get(species).gen3?.flavorText` and species height / color /
 * growth-rate fields — all phantom in `@pkmn/dex`. SpeciesData only
 * carries abilities, baseStats, eggGroups, num, name, types, weightkg.
 * No flavor, no height, no color, no growth rate.
 *
 * Mitigation: hand-author the missing data for v0.1's 12 species from the
 * FireRed dex (the substrate). Each entry verified against Bulbapedia's
 * FRLG dex transcription. Future-species additions (TODOs #17 etc.) must
 * extend this table — the dossier ABOUT tab degrades to skipping the
 * pull-quote + facts sentence when an entry is missing (SR-6 graceful
 * degradation; no error state).
 *
 * Bundle delta budget: ~3-5 KB raw (~1-2 KB gzip) per EN-2.
 */

export type SpeciesColor =
  | 'green' | 'red' | 'blue' | 'yellow' | 'brown'
  | 'gray'  | 'purple' | 'pink' | 'white' | 'black';

export type GrowthRate =
  | 'slow' | 'medium-slow' | 'medium-fast' | 'fast' | 'erratic' | 'fluctuating';

export interface SpeciesFlavor {
  /** FRLG dex pull-quote, verbatim. Null if no Gen-3 entry exists for the
   *  species (pull-quote slot stays empty silently per Premise 2 fallback). */
  flavorText: string | null;
  /** Height in inches, cartridge-canonical. (Pokémon Gen 3 displays height
   *  in feet+inches; integer inches simplifies the formatter — divides by
   *  12 + modulo to render as `2'04"`. Onix at 346" = 28'10".) */
  heightInches: number;
  /** Cartridge color category (Pokédex "color" filter — visual taxonomy,
   *  not literal sprite color). Used by the ABOUT prose template's
   *  "the green Seed Pokémon" opening when the color sentence fires. */
  color: SpeciesColor;
  /** Cartridge XP growth rate. Used by the ABOUT facts paragraph. */
  growthRate: GrowthRate;
}

/**
 * Per-species flavor + facts table. Keyed by @pkmn-style lowercase species
 * id. Sources: FireRed (FRLG) Pokédex transcribed via Bulbapedia mirror.
 * Growth rates verified against Bulbapedia (e.g. Bulbasaur/Charmander/
 * Squirtle/Pidgey lineages are Medium Slow; remaining v0.1 species are
 * Medium Fast).
 */
export const FLAVOR_TEXT: Record<string, SpeciesFlavor> = {
  bulbasaur: {
    flavorText:
      'A strange seed was planted on its back at birth. The plant sprouts and grows with this Pokémon.',
    heightInches: 28, // 2'04"
    color: 'green',
    growthRate: 'medium-slow',
  },
  charmander: {
    flavorText:
      'The flame that burns at the tip of its tail is an indication of its emotions. The flame wavers when Charmander is enjoying itself.',
    heightInches: 24, // 2'00"
    color: 'red',
    growthRate: 'medium-slow',
  },
  squirtle: {
    flavorText:
      'Shoots water at prey while in the water. Withdraws into its shell when in danger.',
    heightInches: 20, // 1'08"
    color: 'blue',
    growthRate: 'medium-slow',
  },
  pidgey: {
    flavorText:
      'A common sight in forests and woods. It flaps its wings at ground level to kick up blinding sand.',
    heightInches: 12, // 1'00"
    color: 'brown',
    growthRate: 'medium-slow',
  },
  rattata: {
    flavorText:
      'Its hardy vitality lets it live in any kind of environment. Also, it has fangs that grow continuously.',
    heightInches: 12, // 1'00"
    color: 'purple',
    growthRate: 'medium-fast',
  },
  spearow: {
    flavorText:
      'Inept at flying high. However, it can fly around very fast to protect its territory.',
    heightInches: 12, // 1'00"
    color: 'brown',
    growthRate: 'medium-fast',
  },
  pikachu: {
    flavorText:
      'When it is angered, it immediately discharges the energy stored in the pouches in its cheeks.',
    heightInches: 16, // 1'04"
    color: 'yellow',
    growthRate: 'medium-fast',
  },
  caterpie: {
    flavorText:
      'For protection, it releases a horrible stench from the antenna on its head to drive away enemies.',
    heightInches: 12, // 1'00"
    color: 'green',
    growthRate: 'medium-fast',
  },
  weedle: {
    flavorText:
      'Often found in forests, eating leaves. It has a sharp, venomous stinger on its head.',
    heightInches: 12, // 1'00"
    color: 'brown',
    growthRate: 'medium-fast',
  },
  metapod: {
    flavorText:
      'It is waiting for the moment to evolve. At this stage, it can only harden, so it remains motionless to avoid attack.',
    heightInches: 28, // 2'04"
    color: 'green',
    growthRate: 'medium-fast',
  },
  kakuna: {
    flavorText:
      'Almost incapable of moving, this Pokémon can only harden its shell to protect itself from predators.',
    heightInches: 24, // 2'00"
    color: 'yellow',
    growthRate: 'medium-fast',
  },
  onix: {
    flavorText:
      'Burrows at high speed in search of food. The tunnels it leaves are used as homes by Diglett.',
    heightInches: 346, // 28'10"
    color: 'gray',
    growthRate: 'medium-fast',
  },
};

/**
 * Resolve a species id to its hand-authored flavor record. Case-insensitive.
 * Returns null when the species has no entry (content gap — caller degrades
 * gracefully per SR-6: skip the pull-quote + facts sentence).
 */
export function getFlavor(species: string): SpeciesFlavor | null {
  if (!species) return null;
  return FLAVOR_TEXT[species.toLowerCase()] ?? null;
}
